import React, { ComponentProps, FC, useState } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material';

import ChevronTopIcon from '~/assets/icons/ChevronTopIcon';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN_PX } from '~/theme';

interface FilterWrapperProps extends ComponentProps<typeof Accordion> {
  title: string;
  withoutToggle?: boolean;
  'data-testid'?: string;
}

const FilterWrapper: FC<FilterWrapperProps> = ({
  title,
  children,
  className,
  withoutToggle = false,
  id,
  ...rest
}) => {
  const theme = useTheme();
  const [filterOpened, setFilterOpened] = useState(true);

  const toggleFilter = () => {
    !withoutToggle && setFilterOpened((prevState) => !prevState);
  };

  return (
    <StyledAccordion
      expanded={filterOpened}
      onChange={toggleFilter}
      className={className}
      classes={{ disabled: 'wrapperDisabled' }}
      disabled={withoutToggle}
      data-testid={rest['data-testid']}
    >
      <StyledAccordionSummary
        id={`${id}Header`}
        aria-controls={`${id}Content`}
        classes={{
          content: 'headingContent',
          expanded: 'headingExpanded',
          disabled: 'headingDisabled',
        }}
      >
        <Title>{title}</Title>
        {!withoutToggle && <Icon color={theme.palette.common.white} filterOpened={filterOpened} />}
      </StyledAccordionSummary>
      <StyledAccordionDetails>{children}</StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default FilterWrapper;

const StyledAccordion = styled(Accordion)`
  background-color: transparent;
  box-shadow: none;
  padding-bottom: ${SMALL_MARGIN_PX};
  margin-bottom: ${SMALL_MARGIN_PX};
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.category.cpd};

  &:before {
    display: none;
  }

  &.wrapperDisabled {
    background: transparent;
    pointer-events: auto;
  }
`;
const StyledAccordionSummary = styled(AccordionSummary)`
  background-color: transparent;
  padding: 0;
  min-height: 28px;

  &.headingExpanded {
    min-height: 28px;
    margin: 0;

    &.headingDisabled {
      opacity: 1;
    }
  }

  & .headingContent {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0;

    &.headingExpanded {
      margin: 0;
      margin-bottom: ${MIDDLE_MARGIN_PX};
    }
  }

  transition: 0.3s color;
  &:hover {
    color: ${({ theme }) => theme.palette.common.accent};
  }
`;
const Title = styled(Typography)`
  font-size: 22px;
  line-height: 1.2;
  margin-bottom: 0;
  font-weight: bold;
`;
const Icon = styled(ChevronTopIcon, {
  shouldForwardProp: (prop) => prop !== 'filterOpened',
})`
  color: inherit;
  width: 16px;
  height: 16px;
  cursor: pointer;
  transform: ${({ filterOpened }) => (filterOpened ? 'none' : 'rotate(180deg)')};
  transition: transform 0.3s ease-out;
`;
const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  & > div:last-child {
    margin-bottom: 0;
  }
`;
